import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Navbar from '../../components/navbar';
import { FaEdit, FaTrash, FaDownload, FaUser, FaCar, FaCalendarAlt, FaPlus, FaMinus } from 'react-icons/fa';
import { toast } from 'react-hot-toast';

function AssignTask() {
  const [serviceDetails, setServiceDetails] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTechnician, setSelectedTechnician] = useState({});
  const [userDetails, setUserDetails] = useState({});
  const [editServiceId, setEditServiceId] = useState(null);
  const [editServiceData, setEditServiceData] = useState({});
  const [filterOption, setFilterOption] = useState('unassigned');
  const [deleteServiceId, setDeleteServiceId] = useState(null);
  const [sortOrder, setSortOrder] = useState('latest');
  const [expandedService, setExpandedService] = useState(null);
  
  const API_URL = process.env.REACT_APP_API_URL;

  // Fields to exclude from display and editing
  const EXCLUDED_FIELDS = [
    '_id', '__v', 'userId', 'technicianId', 'pdiReportId', 
    'createdAt', 'updatedAt', 'vehicleImageUrl'
  ];

  // Fetch service details
  const fetchServiceDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/services/getAll`);
      const services = response.data;
      setServiceDetails(services);

      // Fetch user details for each service
      const userFetchPromises = services.map(async (service) => {
        const userId = service.userId && typeof service.userId === 'object' ? service.userId._id : null;
        if (userId) {
          await getUser(userId);
        }
      });

      await Promise.all(userFetchPromises);
    } catch (err) {
      setError('Error fetching service details');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServiceDetails();
  }, []);

  // Fetch technicians
  useEffect(() => {
    const fetchTechnicians = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/admin/technicians`);
        if (Array.isArray(response.data)) {
          setTechnicians(response.data);
        } else {
          setError('Technicians data is not in the expected format');
        }
      } catch (err) {
        setError('Error fetching technicians');
        console.error('Technician fetch error:', err);
      }
    };

    fetchTechnicians();
  }, []);

  // Get user details
  const getUser = async (userId) => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/users/${userId}/get`);
      setUserDetails((prevState) => ({
        ...prevState,
        [userId]: response.data,
      }));
    } catch (err) {
      console.error(`Error fetching user details for userId ${userId}:`, err);
    }
  };

  // Assign service to technician
  const assignServiceToTechnician = async (serviceId, technicianId) => {
    try {
      const response = await axios.patch(`${API_URL}/api/services/${serviceId}/assign-technician`, { technicianId });
      fetchServiceDetails();
      // The assignment itself succeeded whenever this call returns 200 — a
      // failed WhatsApp notification no longer turns into an error response
      // (see assignTechnicianToService on the backend). Surface that as a
      // warning, not a success, so the admin knows to reach the technician
      // another way, but never as a failure since the assignment did go through.
      if (response.data?.notificationSent === false) {
        // react-hot-toast has no built-in "warning" variant — use the
        // generic toast with a warning icon so this reads as a caution,
        // not a success or a failure.
        toast('Technician assigned successfully. WhatsApp notification could not be sent — please contact the technician manually.', { icon: '⚠️' });
      } else {
        toast.success('Technician assigned successfully');
      }
    } catch (err) {
      setError(`Error assigning technician to service ${serviceId}`);
      console.error('Assign error:', err);
      toast.error('Failed to assign technician');
    }
  };

  const handleTechnicianChange = (serviceId, technicianId) => {
    setSelectedTechnician((prevState) => ({
      ...prevState,
      [serviceId]: technicianId,
    }));
  };

  // Handle edit task submission
  const handleEditTask = async () => {
    try {
      const formData = new FormData();
      const serviceId = editServiceId;
      
      // Append all fields except excluded ones
      Object.keys(editServiceData).forEach((key) => {
        if (!EXCLUDED_FIELDS.includes(key) && key !== 'vehicleImage') {
          formData.append(key, editServiceData[key]);
        }
      });
      
      // Handle file upload
      if (editServiceData.vehicleImage) {
        formData.append('vehicleImage', editServiceData.vehicleImage);
      }
      
      await axios.patch(`${API_URL}/api/services/${serviceId}/edit`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      fetchServiceDetails();
      setEditServiceId(null);
      toast.success('Service updated successfully');
    } catch (err) {
      console.error('Error updating service:', err);
      toast.error('Failed to update service');
    }
  };  

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditServiceData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle file change for vehicle image
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setEditServiceData((prev) => ({
        ...prev,
        vehicleImage: file,
      }));
    } else if (file) {
      toast.error('Please upload a valid image file');
    }
  };

  // Handle filter change
  const handleFilterChange = (e) => {
    setFilterOption(e.target.value);
  };

  // Filter services based on selection
  const filteredServices = serviceDetails.filter(service => {
    if (filterOption === 'unassigned') {
      return !service.technicianId && service.status === 'Pending';
    } else if (filterOption === 'assigned') {
      return service.technicianId && service.status === 'Assigned';
    } else if (filterOption === 'completed') {
      return service.technicianId && service.status === 'Completed';
    }
    return true;
  });

  // Sort services by date
  const sortedServices = [...filteredServices].sort((a, b) => {
    const dateA = new Date(a.deliveryDate);
    const dateB = new Date(b.deliveryDate);
    return sortOrder === 'latest' ? dateB - dateA : dateA - dateB;
  });

  // Handle service deletion
  const handleDeleteService = async () => {
    try {
      await axios.delete(`${API_URL}/api/services/${deleteServiceId}`);
      setServiceDetails((prev) => prev.filter((service) => service._id !== deleteServiceId));
      setDeleteServiceId(null);
      toast.success('Service deleted successfully');
    } catch (err) {
      console.error('Error deleting service:', err);
      toast.error('Failed to delete service');
    }
  };

  // Toggle service details accordion
  const toggleAccordion = (serviceId) => {
    setExpandedService(expandedService === serviceId ? null : serviceId);
  };

  // Format field names for display
  const formatFieldName = (fieldName) => {
    const result = fieldName.replace(/([A-Z])/g, ' $1');
    return result.charAt(0).toUpperCase() + result.slice(1);
  };

  // Format field values for display
  const formatFieldValue = (key, value) => {
    if (value === null || value === undefined) return 'N/A';
    
    // Format dates
    if (key.toLowerCase().includes('date')) {
      return new Date(value).toLocaleDateString();
    }
    
    return value;
  };

  // Get all fields from a service object except excluded ones
  const getServiceFields = (service) => {
    return Object.entries(service)
      .filter(([key]) => !EXCLUDED_FIELDS.includes(key))
      .map(([key, value]) => ({
        name: key,
        label: formatFieldName(key),
        value: formatFieldValue(key, value)
      }));
  };

  // Group fields into sections for better organization
  const groupFields = (fields) => {
    const customerFields = fields.filter(field => 
      field.name.toLowerCase().includes('customer') || 
      field.name.toLowerCase().includes('user')
    );
    
    const vehicleFields = fields.filter(field => 
      field.name.toLowerCase().includes('vehicle') || 
      field.name.toLowerCase().includes('model') ||
      field.name.toLowerCase().includes('make') ||
      field.name.toLowerCase().includes('variant') ||
      field.name.toLowerCase().includes('vin') ||
      field.name.toLowerCase().includes('fuel') ||
      field.name.toLowerCase().includes('engine') ||
      field.name.toLowerCase().includes('bhp') ||
      field.name.toLowerCase().includes('ncap') ||
      field.name.toLowerCase().includes('mileage')
    );
    
    const serviceFields = fields.filter(field => 
      field.name.toLowerCase().includes('service') || 
      field.name.toLowerCase().includes('inspection') ||
      field.name.toLowerCase().includes('status') ||
      field.name.toLowerCase().includes('dealer') ||
      field.name.toLowerCase().includes('delivery')
    );
    
    const otherFields = fields.filter(field => 
      !customerFields.includes(field) && 
      !vehicleFields.includes(field) && 
      !serviceFields.includes(field)
    );
    
    return [
      { title: 'Customer Information', icon: <FaUser className="mr-2" />, fields: customerFields },
      { title: 'Vehicle Details', icon: <FaCar className="mr-2" />, fields: vehicleFields },
      { title: 'Service Information', icon: <FaCalendarAlt className="mr-2" />, fields: serviceFields },
      { title: 'Additional Details', icon: <FaPlus className="mr-2" />, fields: otherFields }
    ];
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="p-6 pt-28 max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800 mb-4 md:mb-0">Task Assignment</h1>
          
          <div className="flex flex-wrap gap-4">
            <div className="w-full md:w-auto">
              <label htmlFor="filter" className="block text-sm font-medium text-gray-700 mb-1">Filter Tasks</label>
              <select 
                id="filter" 
                value={filterOption} 
                onChange={handleFilterChange}
                className="w-full md:w-48 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="unassigned">Unassigned Tasks</option>
                <option value="assigned">Assigned Tasks</option>
                <option value="completed">Completed Tasks</option>
                <option value="all">All Tasks</option>
              </select>
            </div>
            
            <div className="w-full md:w-auto">
              <label htmlFor="sort" className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
              <select 
                id="sort" 
                value={sortOrder} 
                onChange={(e) => setSortOrder(e.target.value)}
                className="w-full md:w-48 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="latest">Latest First</option>
                <option value="oldest">Oldest First</option>
              </select>
            </div>
          </div>
        </div>

        {loading && (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        )}
        
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {!loading && sortedServices.length === 0 && (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="mt-2 text-lg font-medium text-gray-900">No tasks found</h3>
            <p className="mt-1 text-sm text-gray-500">
              There are currently no {filterOption} tasks.
            </p>
          </div>
        )}

        <div className="space-y-4">
          {sortedServices.map((service) => {
            const userId = service.userId && typeof service.userId === 'object' ? service.userId._id : null;
            const userDetail = userDetails[userId];
            const allFields = getServiceFields(service);
            const groupedFields = groupFields(allFields);
            
            return (
              <div key={service._id} className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow">
                <button
                  className="w-full flex justify-between items-center p-4 bg-white hover:bg-gray-50 transition-colors"
                  onClick={() => toggleAccordion(service._id)}
                >
                  <div className="text-left">
                    <div className="flex items-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mr-2 ${
                        service.status === 'Completed' 
                          ? 'bg-green-100 text-green-800' 
                          : service.status === 'Assigned' 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {service.status}
                      </span>
                      <h3 className="font-semibold text-lg text-gray-900">
                        {service.customerName || 'Unnamed Service'}
                      </h3>
                    </div>
                    <p className="text-sm text-gray-600 mt-1 flex items-center">
                      <FaCar className="mr-1 text-gray-500" />
                      {service.vehicleModel} • {service.inspectionType}
                    </p>
                  </div>
                  <span className="text-xl text-gray-900 transform transition-transform">
                    {expandedService === service._id ? <FaMinus /> : <FaPlus />}
                  </span>
                </button>

                {expandedService === service._id && (
                  <div className="p-4 bg-gray-50 border-t border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {groupedFields.map((section, idx) => (
                        section.fields.length > 0 && (
                          <div key={idx} className="bg-white rounded-lg p-4 shadow-xs border border-gray-200">
                            <div className="flex items-center text-gray-700 font-medium mb-3">
                              {section.icon}
                              {section.title}
                            </div>
                            <div className="space-y-2 text-sm">
                              {section.fields.map((field) => (
                                <div key={field.name} className="flex">
                                  <span className="font-medium text-gray-600 w-1/3">{field.label}:</span>
                                  <span className="text-gray-900 w-2/3">{field.value}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )
                      ))}
                      
                      {/* User Details */}
                      <div className="bg-white rounded-lg p-4 shadow-xs border border-gray-200">
                        <div className="flex items-center text-gray-700 font-medium mb-3">
                          <FaUser className="mr-2" />
                          User Information
                        </div>
                        {userDetail ? (
                          <div className="space-y-2 text-sm">
                            <div className="flex">
                              <span className="font-medium text-gray-600 w-1/3">Name:</span>
                              <span className="text-gray-900 w-2/3">{userDetail.firstname} {userDetail.lastname}</span>
                            </div>
                            <div className="flex">
                              <span className="font-medium text-gray-600 w-1/3">Email:</span>
                              <span className="text-gray-900 w-2/3">{userDetail.email}</span>
                            </div>
                            <div className="flex">
                              <span className="font-medium text-gray-600 w-1/3">Phone:</span>
                              <span className="text-gray-900 w-2/3">{userDetail.phone || 'N/A'}</span>
                            </div>
                          </div>
                        ) : (
                          <p className="text-gray-500">Loading user details...</p>
                        )}
                      </div>
                      
                      {/* Vehicle Image */}
                      {service.vehicleImageUrl && (
                        <div className="bg-white rounded-lg p-4 shadow-xs border border-gray-200">
                          <div className="flex items-center text-gray-700 font-medium mb-3">
                            <FaCar className="mr-2" />
                            Vehicle Image
                          </div>
                          <div className="flex justify-center">
                            <img 
                              src={service.vehicleImageUrl} 
                              alt={`${service.vehicleModel}`} 
                              className="w-64 h-auto object-contain rounded-md border border-gray-200"
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Technician Assignment */}
                    {service.status !== 'Completed' && (
                      <div className="mt-6 bg-white rounded-lg p-4 shadow-xs border border-gray-200">
                        <h4 className="font-medium text-gray-700 mb-3">Technician Assignment</h4>
                        
                        {service.technicianId ? (
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div className="flex items-center">
                              <div className="bg-gray-200 border-2 border-dashed rounded-xl w-16 h-16 flex items-center justify-center text-gray-500">
                                <FaUser className="text-2xl" />
                              </div>
                              <div className="ml-4">
                                <p className="font-medium text-gray-900">
                                  {technicians.find(t => t._id === service.technicianId?._id)?.firstname || 'Unknown'} 
                                  {' '}
                                  {technicians.find(t => t._id === service.technicianId?._id)?.lastname || 'Technician'}
                                </p>
                                <p className="text-sm text-gray-500">Assigned Technician</p>
                              </div>
                            </div>
                            
                            <div className="flex flex-col md:flex-row gap-3">
                              <select
                                value={selectedTechnician[service._id] || ''}
                                onChange={(e) => handleTechnicianChange(service._id, e.target.value)}
                                className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="" disabled>Change Technician</option>
                                {technicians.map((technician) => (
                                  <option key={technician._id} value={technician._id}>
                                    {technician.firstname} {technician.lastname}
                                  </option>
                                ))}
                              </select>
                              
                              <button
                                onClick={() => assignServiceToTechnician(service._id, selectedTechnician[service._id])}
                                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={!selectedTechnician[service._id]}
                              >
                                Reassign
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <p className="text-gray-600">No technician assigned</p>
                            
                            <div className="flex flex-col md:flex-row gap-3">
                              <select
                                value={selectedTechnician[service._id] || ''}
                                onChange={(e) => handleTechnicianChange(service._id, e.target.value)}
                                className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="" disabled>Select Technician</option>
                                {technicians.map((technician) => (
                                  <option key={technician._id} value={technician._id}>
                                    {technician.firstname} {technician.lastname}
                                  </option>
                                ))}
                              </select>
                              
                              <button
                                onClick={() => assignServiceToTechnician(service._id, selectedTechnician[service._id])}
                                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={!selectedTechnician[service._id]}
                              >
                                Assign
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                  </div>
                )}

                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-3 mt-2 p-4 border-t border-gray-200">
                      <button
                        onClick={() => {
                          setEditServiceId(service._id);
                          setEditServiceData({ ...service });
                        }}
                        className="flex items-center gap-2 bg-white border border-gray-300 rounded-md px-4 py-2 transition-colors text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                      >
                        <FaEdit />
                        Edit Service
                      </button>
                      
                      <button
                        onClick={() => setDeleteServiceId(service._id)}
                        className="flex items-center gap-2 bg-white border border-gray-300 rounded-md px-4 py-2 text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <FaTrash />
                        Delete
                      </button>
                    </div>

              </div>
            );
          })}
        </div>
      </div>

      {/* Edit Service Modal */}
      {editServiceId && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b border-gray-200 p-4 sticky top-0 bg-white z-10">
              <h3 className="text-xl font-semibold text-gray-800">Edit Service Details</h3>
              <button 
                onClick={() => setEditServiceId(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {getServiceFields(editServiceData).map((field) => {
                  // Determine input type based on field name
                  let inputType = 'text';
                  if (field.name.toLowerCase().includes('date')) inputType = 'date';
                  if (field.name.toLowerCase().includes('email')) inputType = 'email';
                  
                  return (
                    <div 
                      key={field.name} 
                      className={field.name.toLowerCase().includes('address') || 
                                 field.name.toLowerCase().includes('custommessage') ? 
                                 'md:col-span-2' : ''}
                    >
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {field.label}
                      </label>
                      
                      {field.name.toLowerCase().includes('address') || 
                       field.name.toLowerCase().includes('custommessage') ? (
                        <textarea
                          name={field.name}
                          value={editServiceData[field.name] || ''}
                          onChange={handleEditInputChange}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          rows="3"
                        />
                      ) : (
                        <input
                          type={inputType}
                          name={field.name}
                          value={editServiceData[field.name] || ''}
                          onChange={handleEditInputChange}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      )}
                    </div>
                  );
                })}
                
                {/* Dealer Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dealer Type
                  </label>
                  <select
                    name="dealerType"
                    value={editServiceData.dealerType || ''}
                    onChange={handleEditInputChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Dealer Type</option>
                    <option value="Showroom">Showroom</option>
                    <option value="Stockyard">Stockyard</option>
                  </select>
                </div>
                
                {/* Vehicle Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vehicle Type
                  </label>
                  <select
                    name="vehicleType"
                    value={editServiceData.vehicleType || ''}
                    onChange={handleEditInputChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Vehicle Type</option>
                    <option value="Luxury">Luxury</option>
                    <option value="Ordinary">Ordinary</option>
                  </select>
                </div>
                
                {/* Vehicle Image */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vehicle Image
                  </label>
                  <div className="flex flex-col md:flex-row gap-4 items-start">
                    {editServiceData.vehicleImageUrl && (
                      <div className="flex-shrink-0">
                        <img 
                          src={editServiceData.vehicleImageUrl} 
                          alt="Current vehicle" 
                          className="w-32 h-32 object-contain border border-gray-300 rounded-md"
                        />
                        <p className="text-xs text-gray-500 mt-1">Current Image</p>
                      </div>
                    )}
                    
                    <div className="flex-1">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {editServiceData.vehicleImage ? 
                         `Selected: ${editServiceData.vehicleImage.name}` : 
                         'Upload a new image (optional)'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-8 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setEditServiceId(null)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditTask}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteServiceId && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex flex-col items-center">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">Delete Service</h3>
              <div className="mt-2 text-sm text-gray-500 text-center">
                <p>Are you sure you want to delete this service?</p>
                <p className="font-medium mt-1">This action cannot be undone.</p>
              </div>
            </div>
            
            <div className="mt-6 flex justify-center space-x-3">
              <button
                onClick={() => setDeleteServiceId(null)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteService}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                Delete Service
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AssignTask;